/**
 * Procedural stone-brick wall material. Generates canvas-based albedo /
 * normal / roughness maps for a running-bond stone wall at module load, then
 * each instance clones the texture wrappers so it can set its own `repeat`
 * scale (back/front walls and side walls have different aspect ratios) while
 * sharing the underlying image data in memory.
 *
 * Pattern: 4 bricks per row × 6 rows per canvas tile, odd rows offset by
 * half a brick width (running bond). Per-brick color variation, dirt
 * speckle, inner-edge darkening, and a matching normal map where each
 * brick chamfers down into the grout so the torch and sun lights catch
 * every course.
 *
 * The wrap math uses a per-brick PRNG seeded by (row, slotIdx mod brickCols)
 * so the half-brick at the left edge of an odd row exactly matches the
 * half-brick at the right edge — the texture tiles seamlessly.
 */
import { useEffect, useMemo } from "react";
import * as THREE from "three";

interface CachedTextures {
  albedo: THREE.CanvasTexture;
  normal: THREE.CanvasTexture;
  rough: THREE.CanvasTexture;
}

let cached: CachedTextures | null = null;
function getCachedTextures(): CachedTextures {
  if (!cached) cached = buildBrickTextures(512, 4, 6);
  return cached;
}

interface ProceduralStoneWallMaterialProps {
  repeat: [number, number];
  color?: string;
}

export function ProceduralStoneWallMaterial({
  repeat,
  color = "#9a8a72",
}: ProceduralStoneWallMaterialProps): JSX.Element {
  const { albedo, normal, rough } = useMemo(() => {
    const src = getCachedTextures();
    return {
      albedo: src.albedo.clone(),
      normal: src.normal.clone(),
      rough: src.rough.clone(),
    };
  }, []);

  useEffect(() => {
    albedo.colorSpace = THREE.SRGBColorSpace;
    for (const t of [albedo, normal, rough]) {
      t.wrapS = THREE.RepeatWrapping;
      t.wrapT = THREE.RepeatWrapping;
      t.repeat.set(repeat[0], repeat[1]);
      t.anisotropy = 8;
      t.needsUpdate = true;
    }
    return () => {
      albedo.dispose();
      normal.dispose();
      rough.dispose();
    };
  }, [albedo, normal, rough, repeat]);

  return (
    <meshStandardMaterial
      map={albedo}
      normalMap={normal}
      normalScale={new THREE.Vector2(0.75, 0.75)}
      roughnessMap={rough}
      roughness={1.0}
      color={color}
    />
  );
}

function makeBrickPrng(row: number, slotIdx: number): () => number {
  let s = (row * 7919 + slotIdx * 631 + 12345) >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

function buildBrickTextures(
  size: number,
  brickCols: number,
  brickRows: number,
): CachedTextures {
  const brickW = size / brickCols;
  const brickH = size / brickRows;
  const groutPx = Math.max(2, Math.floor(size / 128));
  const bevelPx = Math.max(3, Math.floor(brickH * 0.18));

  // === Albedo ===
  const albCanvas = document.createElement("canvas");
  albCanvas.width = size;
  albCanvas.height = size;
  const ac = albCanvas.getContext("2d")!;

  ac.fillStyle = "#2a2218"; // grout
  ac.fillRect(0, 0, size, size);

  for (let row = 0; row < brickRows; row++) {
    const yRect = row * brickH + groutPx;
    const h = brickH - groutPx * 2;
    const oddRow = row % 2 === 1;
    const xShift = oddRow ? -brickW / 2 : 0;

    // Include k = -1 and k = brickCols for the wrapping half-bricks at the
    // edges of an odd row. Both resolve to the same slotIdx as their
    // counterpart on the opposite edge, so colors line up at the seam.
    for (let k = -1; k <= brickCols; k++) {
      const slotIdx = ((k % brickCols) + brickCols) % brickCols;
      const xRect = k * brickW + xShift + groutPx;
      const w = brickW - groutPx * 2;
      if (xRect + w < 0 || xRect > size) continue;

      const rand = makeBrickPrng(row, slotIdx);
      const hue = 30 + (rand() - 0.5) * 8;
      const sat = 16 + rand() * 12;
      const light = 42 + rand() * 18;
      const tileColor = `hsl(${hue}, ${sat}%, ${light}%)`;

      ac.fillStyle = tileColor;
      ac.fillRect(xRect, yRect, w, h);

      const gx = ac.createLinearGradient(xRect, yRect, xRect + w, yRect);
      gx.addColorStop(0, "rgba(0,0,0,0.30)");
      gx.addColorStop(0.15, "rgba(0,0,0,0)");
      gx.addColorStop(0.85, "rgba(0,0,0,0)");
      gx.addColorStop(1, "rgba(0,0,0,0.30)");
      ac.fillStyle = gx;
      ac.fillRect(xRect, yRect, w, h);

      const gy = ac.createLinearGradient(xRect, yRect, xRect, yRect + h);
      gy.addColorStop(0, "rgba(0,0,0,0.30)");
      gy.addColorStop(0.18, "rgba(0,0,0,0)");
      gy.addColorStop(0.82, "rgba(0,0,0,0)");
      gy.addColorStop(1, "rgba(0,0,0,0.30)");
      ac.fillStyle = gy;
      ac.fillRect(xRect, yRect, w, h);

      ac.save();
      ac.beginPath();
      ac.rect(xRect, yRect, w, h);
      ac.clip();
      for (let n = 0; n < 50; n++) {
        const nx = xRect + rand() * w;
        const ny = yRect + rand() * h;
        const nr = 1 + rand() * 3;
        const dark = rand() > 0.55;
        ac.globalAlpha = 0.05 + rand() * 0.08;
        ac.fillStyle = dark ? "#1a1208" : "#fff0d8";
        ac.beginPath();
        ac.arc(nx, ny, nr, 0, Math.PI * 2);
        ac.fill();
      }
      ac.restore();
      ac.globalAlpha = 1;
    }
  }

  // === Normal map ===
  const normCanvas = document.createElement("canvas");
  normCanvas.width = size;
  normCanvas.height = size;
  const nc = normCanvas.getContext("2d")!;
  const nImg = nc.createImageData(size, size);
  const nData = nImg.data;

  for (let py = 0; py < size; py++) {
    const row = Math.floor(py / brickH);
    const inRowY = py - row * brickH;
    const oddRow = row % 2 === 1;
    const xShift = oddRow ? -brickW / 2 : 0;

    for (let px = 0; px < size; px++) {
      const adjX = px - xShift;
      const slotPos = ((adjX % brickW) + brickW) % brickW;

      const inGroutX = slotPos < groutPx || slotPos >= brickW - groutPx;
      const inGroutY = inRowY < groutPx || inRowY >= brickH - groutPx;

      let nx = 0;
      let ny = 0;
      let nz = 1;

      if (!inGroutX && !inGroutY) {
        const distLeft = slotPos - groutPx;
        const distRight = brickW - groutPx - slotPos;
        const distTop = inRowY - groutPx;
        const distBot = brickH - groutPx - inRowY;
        const tiltX =
          Math.max(0, 1 - distLeft / bevelPx) -
          Math.max(0, 1 - distRight / bevelPx);
        const tiltY =
          Math.max(0, 1 - distTop / bevelPx) -
          Math.max(0, 1 - distBot / bevelPx);
        const strength = 0.55;
        nx = tiltX * strength;
        ny = tiltY * strength;
        const mag2 = nx * nx + ny * ny;
        nz = mag2 < 1 ? Math.sqrt(1 - mag2) : 0;
      }

      const idx = (py * size + px) * 4;
      nData[idx + 0] = Math.round((nx * 0.5 + 0.5) * 255);
      nData[idx + 1] = Math.round((ny * 0.5 + 0.5) * 255);
      nData[idx + 2] = Math.round(nz * 255);
      nData[idx + 3] = 255;
    }
  }
  nc.putImageData(nImg, 0, 0);

  // === Roughness ===
  const roughCanvas = document.createElement("canvas");
  roughCanvas.width = size;
  roughCanvas.height = size;
  const rc = roughCanvas.getContext("2d")!;
  rc.fillStyle = "#e8e8e8"; // grout matte
  rc.fillRect(0, 0, size, size);

  for (let row = 0; row < brickRows; row++) {
    const yRect = row * brickH + groutPx;
    const h = brickH - groutPx * 2;
    const oddRow = row % 2 === 1;
    const xShift = oddRow ? -brickW / 2 : 0;
    for (let k = -1; k <= brickCols; k++) {
      const slotIdx = ((k % brickCols) + brickCols) % brickCols;
      const xRect = k * brickW + xShift + groutPx;
      const w = brickW - groutPx * 2;
      if (xRect + w < 0 || xRect > size) continue;
      const baseSeed = (row * 7919 + slotIdx * 631) >>> 0;
      const rough = 0xc0 + (baseSeed % 0x40);
      const hex = rough.toString(16).padStart(2, "0");
      rc.fillStyle = `#${hex}${hex}${hex}`;
      rc.fillRect(xRect, yRect, w, h);
    }
  }

  return {
    albedo: new THREE.CanvasTexture(albCanvas),
    normal: new THREE.CanvasTexture(normCanvas),
    rough: new THREE.CanvasTexture(roughCanvas),
  };
}
